import React, { useEffect, useState, useRef } from 'react'
import EnterpriseIcon, { SearchIcon, CancelIcon } from '@enterprise-ui/icons'
import { Button, Table, Input, Tooltip } from '@enterprise-ui/canvas-ui-react'
import { findIndex, cloneDeep, isEqual, isEmpty } from 'lodash'
import { hasFields } from 'util/Filter'

const EditableField = ({
  editOpen = false,
  editRow = -1,
  editEnabled = false,
  selectedItemIndex = -1,
  row = {},
  handleChange = () => {},
  customKey = '',
  editItems = [],
  uniqueIdentifier = '',
  updateInfo = {},
}) => {
  return editOpen &&
    editRow === selectedItemIndex &&
    editEnabled &&
    editItems.indexOf(customKey) !== -1 ? (
    <Input.Text
      value={
        updateInfo[uniqueIdentifier] === row[uniqueIdentifier]
          ? updateInfo[customKey]
          : row[customKey]
      }
      onChange={(event) => {
        handleChange(customKey, row, event)
      }}
    />
  ) : (
    <span>{row[customKey]}</span>
  )
}

const CustomEditableCanvasTable = ({
  columnData = [],
  customRowCount = '',
  data = [],
  dataFieldKeys = [],
  disableTablePagination = false,
  uniqueIdentifierInData = '',
  editEnabled = false,
  editItems = [],
  enableSearch = false,
  enableMultiSearch = false,
  showPopup = () => {},
  totalRowsCount = 0,
  updateData = () => {},
  isLoading = false,
  checkBoxEnabled = false,
  onSelected = () => {},
  selected = [],
  onCheckBoxChange = () => {},
  onCheckBoxHeaderChange = () => {},
  ...restProps
}) => {
  const [editableObject, setEditableObject] = useState({})
  const [updateInfo, setUpdateInfo] = useState({})

  const [searchText, setSearchText] = useState('')
  const [searchOpen, setsearchOpen] = useState(false)

  const [filterData, setFilterData] = useState(data || [])

  const handleChange = (event) => {
    let filterValue = event.target.value
    let searchQuery = filterValue
    if (enableMultiSearch) {
      try {
        searchQuery = filterValue
          ?.replace(/[/+//()/[\\]/g, '\\$&') // Escapes special characters
          ?.trim()
          .split(/[,\r\n]+/g) // Regex to split by comma and newline
          ?.filter(String) // removes empty string from search list
        const wholeString = searchQuery?.join(' ') // Add whole string in case space separated string was searched
        searchQuery.unshift(wholeString)
      } catch (e) {
        console.log('Error in special character search', e)
      }
    }
    setSearchText(filterValue)
    filterDataFunction(searchQuery)
  }

  const filterDataFunction = (searchQuery = '') => {
    let result = [...data]
    if (searchQuery) {
      if (
        enableMultiSearch &&
        Array.isArray(searchQuery) &&
        searchQuery.length > 0
      ) {
        // If list of search query
        result = data.filter((item) =>
          searchQuery.some((value) => hasFields(item, value))
        )
      } else if (!enableMultiSearch) {
        // If single search query
        result =
          data.filter((i) => {
            return hasFields(i, searchQuery)
          }) || []
      }
    }
    setFilterData(result)
  }

  // Update filterData if data prop changes
  useEffect(() => {
    if (!isEqual(filterData, data || [])) {
      filterDataFunction(searchText)
    }
  }, [data])

  const openEdit = (rowNum) => {
    setEditableObject({
      editOpen: true,
      editRow: rowNum,
      deleteOpen: false,
      deleteRow: -1,
    })
  }

  const saveInfo = (updateFlag = false) => {
    if (!updateFlag) {
      updateData(updateInfo)
    }
    setEditableObject({
      ...editableObject,
      editOpen: false,
      editRow: -1,
    })
    setUpdateInfo({})
  }

  const handleChangeUpdateField = (key, item, event) => {
    const stateUpdateInfo = Object.assign({}, updateInfo)
    const itemCopy = Object.assign({}, item)
    const updatedValue = event.target.value || ''
    const updatedUpdateInfo = Object.assign({}, itemCopy, stateUpdateInfo, {
      [key]: updatedValue,
    })
    setUpdateInfo(updatedUpdateInfo)
  }

  const mappedRowData = () => {
    const { editOpen = false, editRow = -1 } = editableObject
    const dataCopy = cloneDeep(filterData)
    return dataCopy.map((row) => {
      const itemCopy = Object.assign({}, row)
      const selectedItemIndex = findIndex(dataCopy, function (obj = {}) {
        return obj[uniqueIdentifierInData] === itemCopy[uniqueIdentifierInData]
      })
      return {
        ...row,
        ...editItems.reduce((acc, key) => {
          acc[key] = {
            cellDisplay: (
              <EditableField
                editOpen={editOpen}
                editRow={editRow}
                selectedItemIndex={selectedItemIndex}
                row={row}
                handleChange={handleChangeUpdateField}
                customKey={key}
                editEnabled={editEnabled}
                editItems={editItems}
                uniqueIdentifier={uniqueIdentifierInData}
                updateInfo={updateInfo}
              />
            ),
            cellValue: row[key] || '',
          }
          return acc
        }, {}),
        edit: {
          cellDisplay: (
            <>
              {editOpen && editRow === selectedItemIndex ? (
                <div style={{ display: 'flex' }}>
                  <Button
                    type="ghost"
                    onClick={() => {
                      saveInfo()
                    }}
                    size="small"
                  >
                    Save
                  </Button>
                  <Button
                    type="ghost"
                    onClick={() => {
                      saveInfo(true)
                    }}
                    size="small"
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <Button
                  type="ghost"
                  onClick={() => openEdit(selectedItemIndex)}
                  size="small"
                >
                  Update
                </Button>
              )}
            </>
          ),
        },
      }
    })
  }
  return (
    <>
      <div className="table-container">
        <div className="table-toolbar" style={{ justifyContent: 'flex-end' }}>
          {enableSearch ? (
            <div style={{ position: 'relative' }}>
              <Tooltip
                style={{
                  display: searchOpen === false ? 'block' : 'none',
                  height: '45px',
                  width: '45px',
                }}
                content="Search list"
                location="bottom"
              >
                <Button
                  type="ghost"
                  iconOnly
                  style={{
                    height: '45px',
                    width: '45px',
                  }}
                >
                  <EnterpriseIcon
                    onClick={() => setsearchOpen(true)}
                    icon={SearchIcon}
                  />
                </Button>
              </Tooltip>
              <Input.Text
                size="dense"
                className="table-search-bar"
                onChange={handleChange}
                placeholder="Search"
                value={searchText}
                style={{
                  display:
                    searchOpen || !isEmpty(searchText) ? 'block' : 'none',
                  width: '400px',
                  height: '45px',
                }}
              />
              <Button
                type="ghost"
                className="search-clear-button icon-button"
                id="clearall"
                style={{
                  display: searchOpen ? 'flex' : 'none',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                size="small"
                onClick={() => {
                  filterDataFunction()
                  setsearchOpen(false)
                  setSearchText('')
                }}
                iconOnly
              >
                <EnterpriseIcon icon={CancelIcon} />
              </Button>
            </div>
          ) : (
            <></>
          )}
        </div>
        <div className="table-content">
          <Table
            data={{
              columnDefs: [
                ...columnData,
                {
                  field: 'edit',
                  headerName: 'Edit',
                },
              ],
              rowData: isLoading ? [] : mappedRowData(filterData),
            }}
            showGrid={false}
            rowSelection="multiple"
            onRowSelect={(rows) => {
              onCheckBoxChange(rows)
            }}
            selectedRows={selected.map((item) => item.id)}
          />
        </div>
      </div>
    </>
  )
}
export default CustomEditableCanvasTable
